import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { useGetAdaptedFictionPreviews } from '../../domains/scene/ui/hooks/useGetAdaptedFictionPreviews';
import { useGetCastRelationshipById } from '../../domains/scene/ui/hooks/useGetCastRelationshipById';
import { useIdentityVerificationMessageListener } from '../identities/hooks/useIdentityVerificationMessageListener';
import { useCastRelationshipId } from './hooks/useCastRelationshipId';
import { useDeleteSceneCommentMessage } from './hooks/useDeleteSceneCommentMessage';
import { usePreviewCarousel } from './hooks/usePreviewCarousel';
import HomeBottomNav from '../../layout/HomeBottomNav';
import NotificationBadge from '../my-page/components/NotificationBadge';
import SwipePreviewCard from './components/SwipePreviewCard';
import FixedSceneCastBar from './components/FixedSceneCastBar';
import CommentBottomSheet from './components/CommentBottomSheet';
import PreviewSubscriptionList from './components/PreviewSubscriptionList';
import SceneOriginalEpisodesList from './components/SceneOriginalEpisodesList';
import { TabSegment } from '../components/tab/TabSegment';

const SCENE_TAB_PARAM = 'tab';

const SceneMainPage = () => {
  useIdentityVerificationMessageListener();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = useMemo(() => {
    const tab = searchParams.get(SCENE_TAB_PARAM);
    return tab === '1' ? 1 : 0;
  }, [searchParams]);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const handleTabChange = (tab: 0 | 1) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(SCENE_TAB_PARAM, String(tab));
        return next;
      },
      { replace: true }
    );
  };

  return (
    <div className="fixed inset-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-[#000000] flex flex-col overflow-hidden overscroll-none">
      <Helmet>
        <title>{t('SceneMain.title')}</title>
      </Helmet>
      <header
        className={`relative z-50 px-[24px] py-[10px] flex items-center justify-between bg-black ${selectedTab === 0 ? 'max-w-[540px]' : ''}`}
      >
        <h1 className="text-[26px] font-bold leading-[31px] text-main-dark-1">
          {t('SceneMain.title')}
        </h1>
        <TabButton selectedTab={selectedTab} handleTabChange={handleTabChange} />
        <NotificationBadge className="!bg-secondary-dark rounded-full !fill-primary-dark" />
      </header>
      <ExploreTabContent
        isActive={selectedTab === 0}
        onKeyboardVisibleChange={setIsKeyboardVisible}
        className={selectedTab !== 0 ? 'hidden' : ''}
      />
      <SavedTabContent className={selectedTab !== 1 ? 'hidden' : ''} />
      {/* biome-ignore lint/a11y/noPositiveTabindex: HomeBottomNav 내부에서 탭 선택용 커스텀 prop으로 사용 */}
      {!isKeyboardVisible && <HomeBottomNav tabIndex={2} />}
    </div>
  );
};

export default SceneMainPage;

interface CommentSheetPreview {
  adaptedFictionEditionId: string;
  adaptedFictionPreviewId: string;
}

const ExploreTabContent = ({
  isActive,
  onKeyboardVisibleChange,
  className
}: {
  isActive: boolean;
  onKeyboardVisibleChange?: (visible: boolean) => void;
  className?: string;
}) => {
  const [selectedPreviewForComment, setSelectedPreviewForComment] =
    useState<CommentSheetPreview | null>(null);
  const castRelationshipId = useCastRelationshipId();
  const { data: castRelationship } = useGetCastRelationshipById(castRelationshipId);

  useDeleteSceneCommentMessage();

  const { data, fetchNextPage, hasNextPage, isFetching } = useGetAdaptedFictionPreviews({
    perPage: 10,
    castRelationshipId
  });

  const previewList = data?.pages?.flat() ?? [];
  const {
    viewportRef,
    loopedItems,
    trackStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTrackTransitionEnd
  } = usePreviewCarousel({
    items: previewList,
    hasNextPage: !!hasNextPage,
    isFetching,
    fetchNextPage
  });

  // 페이지 스크롤 방지: 씬 페이지가 풀스크린일 때 body 스크롤 잠금
  useEffect(() => {
    if (!isActive) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isActive]);

  return (
    <main className={`relative flex-1 overflow-hidden overscroll-none ${className ?? ''}`}>
      <div
        ref={viewportRef}
        className="h-full overflow-hidden touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div
          className="flex h-full flex-col"
          style={trackStyle}
          onTransitionEnd={handleTrackTransitionEnd}
        >
          {loopedItems.map((preview, index) => {
            const isLeadingSentinel = previewList.length > 1 && index === 0;
            const isTrailingSentinel = previewList.length > 1 && index === loopedItems.length - 1;
            const key = isLeadingSentinel
              ? `loop-head-${preview.adaptedFictionPreviewId}-${preview.sequenceNo}`
              : isTrailingSentinel
                ? `loop-tail-${preview.adaptedFictionPreviewId}-${preview.sequenceNo}`
                : `${preview.adaptedFictionPreviewId}-${preview.sequenceNo}`;

            return (
              <div key={key} className="h-full min-h-full flex-none">
                <SwipePreviewCard
                  preview={preview}
                  onOpenCommentBottomSheet={() =>
                    setSelectedPreviewForComment({
                      adaptedFictionEditionId: preview.adaptedFictionEditionId ?? '',
                      adaptedFictionPreviewId: preview.adaptedFictionPreviewId ?? ''
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      </div>
      {selectedPreviewForComment && (
        <CommentBottomSheet
          adaptedFictionEditionId={selectedPreviewForComment.adaptedFictionEditionId}
          adaptedFictionPreviewId={selectedPreviewForComment.adaptedFictionPreviewId}
          isOpen={!!selectedPreviewForComment}
          onClose={() => setSelectedPreviewForComment(null)}
          onKeyboardVisibleChange={onKeyboardVisibleChange}
        />
      )}
      <FixedSceneCastBar castRelationship={castRelationship ?? null} />
    </main>
  );
};

const SAVED_TAB_SEGMENTS = [t('SceneMain.preview'), t('SceneMain.purchaseStory')] as const;
const SAVED_TAB_PARAM = 'savedTab';

const SavedTabContent = ({ className }: { className?: string }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const savedTabIndex = useMemo(() => {
    const savedTab = searchParams.get(SAVED_TAB_PARAM);
    return savedTab === '1' ? 1 : 0;
  }, [searchParams]);
  const selectedBgClass =
    savedTabIndex === 0
      ? '[&_button:first-child]:!bg-pure-black [&_button:first-child]:!text-pure-white'
      : '[&_button:nth-child(2)]:!bg-pure-black [&_button:nth-child(2)]:!text-pure-white';

  return (
    <main className={`flex flex-1 flex-col min-h-0 pb-[70px] ${className ?? ''}`}>
      <div className={`flex-shrink-0 px-[24px] mt-[10px] h-[52px] py-[4px] ${selectedBgClass}`}>
        <TabSegment
          tabIndex={savedTabIndex}
          segments={[...SAVED_TAB_SEGMENTS]}
          onTabChange={(index) => {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set(SAVED_TAB_PARAM, String(index));
                return next;
              },
              { replace: true }
            );
          }}
        />
      </div>
      <div className="flex-1 min-h-0">
        {savedTabIndex === 0 ? <PreviewSubscriptionList /> : <SceneOriginalEpisodesList />}
      </div>
    </main>
  );
};

const TabButton = ({
  selectedTab,
  handleTabChange
}: {
  selectedTab: 0 | 1;
  handleTabChange: (tab: 0 | 1) => void;
}) => {
  const TabClassName =
    'pt-[4px] pb-[3px] text-[15px] font-semibold leading-[18px] text-pure-white border-b';
  return (
    <div className="flex items-center gap-[24px] absolute left-1/2 -translate-x-1/2">
      <button
        type="button"
        className={`${TabClassName} ${selectedTab === 0 ? 'border-pure-white' : 'border-transparent'}`}
        onClick={() => handleTabChange(0)}
      >
        {t('SceneMain.explore')}
      </button>
      <button
        type="button"
        className={`${TabClassName} ${selectedTab === 1 ? 'border-pure-white' : 'border-transparent'}`}
        onClick={() => handleTabChange(1)}
      >
        {t('SceneMain.saved')}
      </button>
    </div>
  );
};
